import React, {Component, ChangeEvent, MouseEvent} from "react"
import {get, filter} from "lodash"

import {getResolvedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import idHelper from "@irati/utils/id"
import FilterCheckboxOption from "@irati/deals/types/FilterCheckboxOption"
import Expander, {ExpanderArrow} from "@irati/ui/src/Expander"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"

import CheckboxGroup from "./CheckboxGroup"
import {Container, InputWithEllipsis} from "./styled"

interface OptionalProps {
  small?: boolean,
  className?: string,
  withBorder?: boolean,
  translateLabels?: boolean,
  id?: string,
  useExpand?: boolean,
  savedFilterOptionsExist?: boolean,
  isAllOptionChecked?: () => void,
  stopDisablingButtonsOnChange?: boolean,
  icon?: IconTypes
}

interface Props extends OptionalProps, InjectIntl {
  loadFilterOptions: Promise<void> | null,
  filterOptions: FilterCheckboxOption[],
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void,
  onSelectAll: (event: ChangeEvent<HTMLInputElement>) => void,
  onReset: () => void,
  onFilter: () => void,
  allOptionsSelectedLabel: string,
  multipleOptionsSelectedLabel: string,
  allOptionText: string,
  resetButtonText?: string,
  saveButtonText?: string
}

interface State {
  disableResetButton: boolean,
  disableSaveButton: boolean,
  label: string | null,
  inFocus: boolean
}

class FilterBy extends Component<Props, State> {
  static defaultProps: OptionalProps = {
    small: false,
    className: "",
    withBorder: false,
    translateLabels: true,
    useExpand: true,
    savedFilterOptionsExist: false,
    id: "deals-filter",
    stopDisablingButtonsOnChange: false
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      disableResetButton: !props.stopDisablingButtonsOnChange,
      disableSaveButton: !props.stopDisablingButtonsOnChange,
      label: null,
      inFocus: false
    }
  }

  UNSAFE_componentWillMount() {
    window.addEventListener("click", this.handleOutsideClick)
  }

  componentDidMount() {
    this.getLabel()
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.filterOptions && this.props.filterOptions) {
      this.getLabel()
    }
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.handleOutsideClick)
  }

  expander = React.createRef<Expander>()

  handleOutsideClick = ({target}: MouseEvent) => {
    if (this.expander.current) {
      if (!this.expander.current.container.current?.contains(target as Node) || !this.expander.current.state.isExpanded) {
        this.expander.current.close()
        return this.setState({inFocus: false})
      }
    }

    this.setState({inFocus: true})
  }

  getLabel = () => {
    const {filterOptions, formatMessage, allOptionsSelectedLabel, multipleOptionsSelectedLabel} = this.props

    const totalFilterOptions = filterOptions ? filterOptions.length : 0
    const checkedFilterOptions = filterOptions ? filterOptions.filter((option: FilterCheckboxOption) => option.isChecked) : []
    let label

    if (checkedFilterOptions.length === totalFilterOptions) {
      label = allOptionsSelectedLabel
    } else if (checkedFilterOptions.length === 1) {
      label = this.labelFromValue(checkedFilterOptions[0].value)
    } else {
      label = formatMessage({id: multipleOptionsSelectedLabel}, {quantity: checkedFilterOptions.length})
    }

    this.setState({label})
  }

  labelFromValue = (value: string) => {
    const {filterOptions, translateLabels} = this.props
    const firstOption = filter(filterOptions, (option) => option.value === value)[0]
    if (!translateLabels) {
      return get(firstOption, "name")
    }
    return getResolvedMessage(this, "name", firstOption)
  }

  updateFilterButtons = () => {
    const {filterOptions, stopDisablingButtonsOnChange} = this.props

    const selectedOptions = filterOptions ? filterOptions.filter((option) => option.isChecked) : []

    const disableResetButton = !filterOptions || !filterOptions.length
    const disableSaveButton = !selectedOptions.length

    this.setState({
      disableResetButton: stopDisablingButtonsOnChange ? false : disableResetButton,
      disableSaveButton: stopDisablingButtonsOnChange ? false : disableSaveButton
    })
  }

  handleSelectFilter = (event: ChangeEvent<HTMLInputElement>) => {
    this.props.onSelect(event)
    this.updateFilterButtons()
  }

  handleSelectAllFilters = (event: ChangeEvent<HTMLInputElement>) => {
    this.props.onSelectAll(event)
    this.updateFilterButtons()
  }

  handleReset = () => {
    this.props.onReset()

    this.setState({
      disableResetButton: true,
      disableSaveButton: true
    })
  }

  handleSave = () => {
    if (this.expander.current) {
      this.expander.current.close()
    }
    this.getLabel()
    this.props.onFilter()

    this.setState({
      disableResetButton: true,
      disableSaveButton: true
    })
  }

  render() {
    const {
      className,
      loadFilterOptions,
      filterOptions,
      allOptionText,
      resetButtonText,
      saveButtonText,
      id,
      useExpand,
      isAllOptionChecked,
      icon
    } = this.props

    const {disableResetButton, disableSaveButton, label, inFocus} = this.state

    const arrow = (props: Props) => <ExpanderArrow {...props} />

    const header = () => (
      <Flex alignItems="center">
        <Icon marginLeft={2} marginRight={2} size={16} icon={icon || IconTypes.funnel} />
        <InputWithEllipsis bold>{label}</InputWithEllipsis>
      </Flex>
    )

    const checkboxGroup = (
      <CheckboxGroup
        id={idHelper("checkbox-group", this.props)}
        loadFilterOptions={loadFilterOptions}
        options={filterOptions}
        allOptionText={allOptionText}
        onSelect={this.handleSelectFilter}
        onSelectAll={this.handleSelectAllFilters}
        resetButtonText={resetButtonText}
        saveButtonText={saveButtonText}
        disableResetButton={disableResetButton}
        disableSaveButton={disableSaveButton}
        onReset={this.handleReset}
        onSave={this.handleSave}
        useExpand={useExpand}
        isAllOptionChecked={isAllOptionChecked} />
    )

    if (useExpand) {
      return (
        <Container id={id} className={className} inFocus={inFocus}>
          <Expander ref={this.expander} fullWidth Header={header} Arrow={arrow}>
            {checkboxGroup}
          </Expander>
        </Container>
      )
    }
    return checkboxGroup
  }
}

export default injectIntl(FilterBy)
